import { movies } from "@/mocks/movies";
import type { ContentType, Movie } from "@/types/movie";

export interface MovieFilters {
  type?: ContentType | "all";
  genre?: string;
  region?: string;
  year?: number | "all";
  keyword?: string;
}

export type SortKey = "release" | "hot" | "rating";

export function getAllMovies(): Movie[] {
  return movies as Movie[];
}

export function getMovieById(id: string): Movie | undefined {
  return getAllMovies().find((movie) => movie.id === id);
}

export function filterMovies(filters: MovieFilters): Movie[] {
  const { type, genre, region, year, keyword } = filters;
  return getAllMovies().filter((movie) => {
    if (type && type !== "all" && movie.type !== type) return false;
    if (genre && genre !== "all" && !movie.genres.includes(genre)) return false;
    if (region && region !== "all" && movie.region !== region) return false;
    if (year && year !== "all" && movie.year !== year) return false;
    if (keyword && keyword.trim()) {
      const key = keyword.trim().toLowerCase();
      const haystack = [
        movie.title,
        movie.director,
        movie.summary,
        movie.region,
        ...movie.actors,
        ...movie.genres,
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(key)) return false;
    }
    return true;
  });
}

export function sortMovies(list: Movie[], sort: SortKey): Movie[] {
  const sorted = [...list];
  if (sort === "rating") {
    sorted.sort((a, b) => b.rating - a.rating);
  } else if (sort === "hot") {
    sorted.sort((a, b) => {
      const hotA = (a.isHot ? 2 : 0) + a.rating;
      const hotB = (b.isHot ? 2 : 0) + b.rating;
      return hotB - hotA;
    });
  } else {
    sorted.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  }
  return sorted;
}

export function getRecommendations(movie: Movie, limit = 6): Movie[] {
  return getAllMovies()
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((g) => movie.genres.includes(g)).length;
      const sharedActors = item.actors.filter((a) => movie.actors.includes(a)).length;
      const sameType = item.type === movie.type ? 1 : 0;
      return { item, score: sharedGenres * 3 + sharedActors * 4 + sameType };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    page: safePage,
  };
}

export function getMoviesByFlag(flag: "isHot" | "isClassic", limit: number): Movie[] {
  return getAllMovies()
    .filter((movie) => movie[flag])
    .sort((a, b) => b.year - a.year)
    .slice(0, limit);
}

export function getLatestMovies(limit: number): Movie[] {
  return sortMovies(getAllMovies(), "release").slice(0, limit);
}

export function getFeaturedMovies(): Movie[] {
  return getAllMovies().filter((movie) => movie.featured);
}